import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import unifiedOfflineService from '@/services/unifiedOfflineService';
import { apiDoublePrefixGuard } from '@/lib/axiosGuards';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Non-nullable flag surfaced from /auth/login → drives the mandatory first-password modal.
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Create api instance with useMemo to properly update when token changes
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
    });

    // Set auth header dynamically + guard against /api double-prefix mistakes
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return apiDoublePrefixGuard(config);
    });

    return instance;
  }, []);

  const fetchUser = useCallback(async (tokenToUse) => {
    const currentToken = tokenToUse || localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(response.data);
      // Survive page refresh: keep the mandatory modal open until the backend
      // says the account has cleared the flag.
      if (response.data && response.data.must_change_password) {
        setMustChangePassword(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser(token);
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, must_change_password } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setMustChangePassword(Boolean(must_change_password));
    
    // Save token to IndexedDB for offline sync capability
    try {
      if (unifiedOfflineService.db) {
        await unifiedOfflineService.saveUserData('authToken', access_token);
        // The Service Worker has no access to process.env, so persist the
        // API base origin alongside the token. Background-sync drains rely
        // on this to know where to POST queued reports.
        await unifiedOfflineService.saveUserData(
          'apiBase',
          process.env.REACT_APP_BACKEND_URL || window.location.origin,
        );
      }
    } catch (e) {
      console.warn('[Auth] Failed to save token to offline storage:', e);
    }
    
    // Immediately fetch user with the new token
    await fetchUser(access_token);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    
    // Clear token from IndexedDB as well
    try {
      if (unifiedOfflineService.db) {
        unifiedOfflineService.saveUserData('authToken', null);
      }
    } catch (e) {
      console.warn('[Auth] Failed to clear token from offline storage:', e);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    api,
    mustChangePassword,
    clearMustChangePassword: () => setMustChangePassword(false),
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
